import React, { useEffect } from 'react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { SmallCloseIcon } from '@chakra-ui/icons';
import { useFormik } from 'formik';

export type PlanFormData = {
  code: string;
  name_zh: string;
  annual_limit: string;
};

interface Props {
  initData?: PlanFormData;
  disableCode?: boolean;
  setFormData: React.Dispatch<React.SetStateAction<PlanFormData | undefined>>;
}

const isValidAnnualLimit = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'unlimited') return true;
  return /^\d+$/.test(trimmed);
};

const PlanEditer: React.FC<Props> = ({ initData, disableCode, setFormData }) => {
  const validate = (values: PlanFormData) => {
    const errors: Partial<Record<keyof PlanFormData, string>> = {};
    if (!values.code.trim()) errors.code = '請填寫方案代碼';
    if (!values.name_zh.trim()) errors.name_zh = '請填寫中文名稱';
    if (!isValidAnnualLimit(values.annual_limit)) errors.annual_limit = '請輸入非負整數或 unlimited';
    return errors;
  };

  const formik = useFormik<PlanFormData>({
    initialValues: { code: '', name_zh: '', annual_limit: '' },
    validate,
    onSubmit: () => {},
    enableReinitialize: false,
  });

  useEffect(() => {
    if (initData) formik.setValues(initData);
  }, [initData]);

  useEffect(() => {
    const isValid =
      Object.keys(formik.errors).length === 0 &&
      formik.values.code.trim() !== '' &&
      formik.values.name_zh.trim() !== '';
    setFormData(isValid ? formik.values : undefined);
  }, [formik.values, formik.errors]);

  const fields: { key: keyof PlanFormData; label: string; placeholder: string; hint?: string; disabled?: boolean }[] = [
    { key: 'code', label: '方案代碼', placeholder: '請輸入方案代碼', disabled: disableCode },
    { key: 'name_zh', label: '中文名稱', placeholder: '請輸入方案中文名稱' },
    {
      key: 'annual_limit',
      label: '年發證上限',
      placeholder: '例如：1000',
      hint: '留空：建立時表示無上限／編輯時表示不變更；輸入 unlimited：設為無上限；其餘請輸入非負整數',
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing="50px">
        {fields.map(({ key, label, placeholder, hint, disabled }) => (
          <Flex key={key} gap="30px">
            <Text
              w="140px"
              minW="140px"
              maxW="140px"
              flexShrink={0}
              flexGrow={0}
              whiteSpace="nowrap"
              wordBreak="keep-all"
              textAlign="left"
              fontWeight="600"
              fontSize="24px"
              lineHeight="133%"
            >
              {label}
            </Text>
            <Flex direction="column" w="100%">
              <FormControl isInvalid={!!formik.errors[key] && formik.touched[key]} isDisabled={disabled}>
                <InputGroup>
                  <Input
                    type="text"
                    name={key}
                    placeholder={placeholder}
                    size="lg"
                    value={formik.values[key]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {!disabled && formik.values[key] && (
                    <InputRightElement cursor="pointer" onClick={() => formik.setFieldValue(key, '')}>
                      <SmallCloseIcon color="primary.main" />
                    </InputRightElement>
                  )}
                </InputGroup>
                {hint && !formik.errors[key] && (
                  <Text fontFamily="'Open Sans', sans-serif" fontSize="13px" color="#4F6470" mt="6px">
                    {hint}
                  </Text>
                )}
                <FormErrorMessage>{formik.errors[key]}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Flex>
        ))}
      </Stack>
    </form>
  );
};

export default PlanEditer;
